import React, { useCallback, useContext, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { RedLinkButton } from '../common';
import {
  FilterCollapsable,
  FilterSearchBar,
  FilterSelect,
  CheckboxGroup,
} from './components';
import {
  backHomeLink,
  defaultLang,
  isPostEvent,
  isQueryFiltersActive,
} from 'utils/constants';
import { useEffect } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  VisitStatusEnum,
  useBookmarkSelector,
} from 'pages/exhibitor/exhibitorBookmarks';
import { ExhibitorContext } from 'context/ExhibitorContextProvider';

const HoveredTypo = styled(Typography)(({ theme, disabled }) => ({
  '&:hover': {
    color: !disabled ? `${theme.palette.primary.main} !important` : 'black',
    cursor: !disabled ? 'pointer' : 'arrow',
  },
}));

const ExhibitorsFilters = ({
  locale,
  onChange,
  filter,
  oldFilters,
  // yearSelected,
  handleMobileFiltersClose = () => {},
  elevationMobileBottom = 0,
  isBiennali = false,
}) => {
  if (!filter) return null;
  // Hooks
  const theme = useTheme();
  const isMobileFilters = useMediaQuery(theme.breakpoints.down(1024));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const intl = useIntl();
  const i18nLegacy = translations.ExhibitorsList(intl).filters;
  const i18n = translations.ExhibitorsV2(intl).Filters;
  const { exhibitorsManage } = useBookmarkSelector();
  const { incognitoMode } = useContext(ExhibitorContext);
  const LOGIN_URL = `${backHomeLink}/${locale}/saml_login?lang=${locale}`;
  const PERSONAL_PAGE_URL = `${backHomeLink}/${locale}/exhibitors?my_list=0`;

  const totExhibitorsToVisit =
    exhibitorsManage.filter((ex) => ex?.status === VisitStatusEnum.TO_VISIT)
      ?.length || 0;

  //States
  const [activeFilters, setActiveFilters] = useState();
  const [activeExhibitor, setActiveExhibitor] = useState();
  const [activeCategory, setActiveCategory] = useState();
  const [activeProduct, setActiveProduct] = useState();
  const submitKeys = ['Enter'];
  //Refs
  const radioVisitsRef = useRef();
  const radioGroupRef = useRef();
  const nationsSelectRef = useRef();
  const itinerariesSelectRef = useRef();
  const productSelectRef = useRef();
  // const yearsSelectRef = useRef();
  const searchRef = useRef();

  useEffect(() => {
    if (filter && activeFilters) {
      onChange && onChange(activeFilters);
    }
  }, [activeFilters]);

  useEffect(() => {
    if (filter && oldFilters) {
      handleInitialValues();
    }
  }, [filter, oldFilters]);

  //Components

  //Functions

  const handleSearchSelection = (text) => {
    const isValid = text !== '';
    setActiveFilters((pre) => ({
      ...pre,
      search: text,
      alphabet: isValid ? null : pre?.alphabet,
      ...(isQueryFiltersActive && {
        pageNumber: 1,
      }),
    }));
  };

  const handleAlphabetSelection = (char) => {
    const isValid = activeFilters?.alphabet !== char;
    setActiveFilters((pre) => ({
      ...pre,
      alphabet: isValid ? char : null,
      search: isValid ? '' : pre.search,
      ...(isQueryFiltersActive && {
        pageNumber: 1,
      }),
    }));
    if (isValid) searchRef.current.customReset();
  };

  const handleNationSelection = (nation) => {
    setActiveFilters((pre) => ({
      ...pre,
      nation: nation ? nation.isoCode : null,
      ...(isQueryFiltersActive && {
        pageNumber: 1,
      }),
    }));
  };

  const handleItinerarySelection = (itinerary) => {
    setActiveFilters((pre) => ({
      ...pre,
      itinerary: itinerary ? itinerary.code : null,
      ...(isQueryFiltersActive && {
        pageNumber: 1,
      }),
    }));
  };

  // const handleYearSelection = (year) => {
  //  handleReset();
  //  onYearChange && onYearChange(year);
  // };

  const handleHallSelection = (hall) => {
    setActiveFilters((pre) => ({
      ...pre,
      hall: pre?.hall !== hall ? hall : null,
      ...(isQueryFiltersActive && {
        pageNumber: 1,
      }),
    }));
  };

  // const handleVisitSlections = (visits) => {
  //   setActiveFilters((pre) => ({
  //     ...pre,
  //     visits: visits.length ? [true] : [],
  //   }));
  // };

  const handleExibitionSelection = (exhibitors) => {
    if (exhibitors.length === 1) {
      const isCatIncluded = activeFilters
        ? exhibitors[0].categories.find(
            (c) => c.code === activeFilters.category
          )
        : false;
      const isProdIncluded = isCatIncluded
        ? exhibitors[0].categories
            .find((c) => c.code === activeFilters.category)
            ?.products.find((p) => p.code === activeFilters.product)
        : false;
      setActiveProduct(isProdIncluded || null);
      if (!isProdIncluded) productSelectRef.current?.customReset();
      setActiveCategory(isCatIncluded || null);
      setActiveExhibitor(exhibitors[0]);
      setActiveFilters((pre) => ({
        ...pre,
        exhibitors: [exhibitors[0].code],
        category: isCatIncluded ? pre.category : null,
        product: isProdIncluded ? pre.product : null,
        ...(isQueryFiltersActive && {
          pageNumber: 1,
        }),
      }));
    } else {
      setActiveProduct(null);
      productSelectRef.current?.customReset();
      setActiveCategory(null);
      setActiveExhibitor(null);
      setActiveFilters((pre) => ({
        ...pre,
        exhibitors: exhibitors.map((e) => e.code),
        category: null,
        product: null,
        ...(isQueryFiltersActive && {
          pageNumber: 1,
        }),
      }));
    }
  };

  const handleCategorySelection = (category) => {
    const isProdIncluded =
      category && category.code === activeFilters.category
        ? category.products.find((p) => p.code === activeFilters.product)
        : false;
    setActiveCategory(category);
    setActiveFilters((pre) => ({
      ...pre,
      category: category ? category.code : null,
      ...(isQueryFiltersActive && {
        pageNumber: 1,
      }),
    }));
    handleProductSelection(isProdIncluded || null);
  };

  const handleProductSelection = (product) => {
    setActiveProduct(product);
    setActiveFilters((pre) => ({
      ...pre,
      product: product ? product.code : null,
      ...(isQueryFiltersActive && {
        pageNumber: 1,
      }),
    }));
    productSelectRef.current?.setInitialValue(product);
  };

  const getLocalizedDescription = (obj) => {
    return getLocalizedParameter(obj, 'description');
  };

  const getLocalizedName = (obj) => {
    return getLocalizedParameter(obj, 'name');
  };
  // const getYear = (str) => {
  //   const yL = filter.yearLabels;
  //   const yearSelected = yL.find((obj) => obj.year === str);
  //   const label = `${
  //     yearSelected?.[`description_${locale}`] ||
  //     yearSelected?.[`description_${defaultLang}`] ||
  //     yearSelected?.description ||
  //     ''
  //   } ${str}`;

  //   return (
  //     <span>
  //       {i18n.yearEdition}: <strong>{label.trim()}</strong>
  //     </span>
  //   );
  // };

  const getLocalizedParameter = (obj, param) => {
    return (
      obj?.[`${param}_${locale}`] ??
      obj?.[`${param}_${defaultLang}`] ??
      `undefined-${param}`
    );
  };

  const getCategories = () => {
    return activeExhibitor.categories;
  };

  const getProducts = () => {
    return activeCategory.products;
  };

  const tryComplete = (e) => {
    if (submitKeys.includes(e.code)) {
      handleSearchSelection(e.target.value);
    }
  };

  const compare = (a, b) => {
    if (
      (a?.[`name_${locale}`] || a?.[`name_${defaultLang}`]) <
      (b?.[`name_${locale}`] || b?.[`name_${defaultLang}`])
    ) {
      return -1;
    }
    if (
      (a?.[`name_${locale}`] || a?.[`name_${defaultLang}`]) >
      (b?.[`name_${locale}`] || b?.[`name_${defaultLang}`])
    ) {
      return 1;
    }
    return 0;
  };

  const compareItineraries = (a, b) => {
    if (
      (a?.[`description_${locale}`] || a?.[`description_${defaultLang}`]) <
      (b?.[`description_${locale}`] || b?.[`description_${defaultLang}`])
    ) {
      return -1;
    }
    if (
      (a?.[`description_${locale}`] || a?.[`description_${defaultLang}`]) >
      (b?.[`description_${locale}`] || b?.[`description_${defaultLang}`])
    ) {
      return 1;
    }
    return 0;
  };

  const orderNations = (array) => array.sort(compare);

  const orderItineraries = (array) => array.sort(compareItineraries);

  // const orderYears = (array) => {
  //   return array.sort((a, b) => {
  //     return parseInt(b) - parseInt(a);
  //   });
  // };

  const handleVisitSlections = (visits) => {
    setActiveFilters((pre) => ({
      ...pre,
      visits: visits.length ? [true] : [],
    }));
  };

  const handleReset = () => {
    setActiveFilters(null);
    setActiveExhibitor(null);
    setActiveCategory(null);
    radioGroupRef.current.groupReset();
    nationsSelectRef.current.customReset();
    itinerariesSelectRef?.current?.customReset();
    searchRef.current.customReset();
    radioVisitsRef?.current?.groupReset();
    if (productSelectRef && productSelectRef.current)
      productSelectRef.current.customReset();
  };

  const handleInitialValues = useCallback(() => {
    setActiveFilters(oldFilters);
    searchRef.current.setInitialValue(oldFilters.search);
    radioGroupRef.current.setInitialValue(oldFilters.exhibitors);
    radioVisitsRef?.current?.setInitialValue(oldFilters.visits);
    nationsSelectRef.current.setInitialValue(
      filter.nations.find((n) => n.isoCode === oldFilters.nation)
    );
    itinerariesSelectRef?.current?.setInitialValue(
      filter.percorsi_tematici?.find((n) => n.code === oldFilters.itinerary)
    );
  }, [oldFilters]);

  const getHoveredTypoStyle = (isSelected) => {
    return {
      color: isSelected ? `${theme.palette.primary.main}` : `black`,
      textDecoration: isSelected ? 'underline' : 'none',
    };
  };

  // const goToList = () => {
  //   window.scrollTo({
  //     top: 0,
  //     left: 0,
  //     behavior: 'smooth',
  //   });
  //   const historyState = history.location.state;
  //   const state = {
  //     ...historyState,
  //     oldFilters: activeFilters,
  //   };
  //   history.push(`/personal-page`, state);
  // };

  return (
    <Box
      padding={isLgUp ? '20px' : '30px 20px'}
      width={'100%'}
      maxWidth={isMobileFilters ? '375px' : '354px'}
      sx={{
        ...(isMobileFilters && {
          margin: '0 auto',
        }),
      }}
    >
      <Stack gap={'40px'} sx={{ position: 'sticky', top: '96px' }}>
        {isMobileFilters ? (
          <Stack
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <RedLinkButton
              msg={`${i18nLegacy.reset}`.toLowerCase()}
              onClick={() => handleReset()}
              variant={'link2'}
              customStyle={{ fontFamily: 'Suisse-Intl !important' }}
            />
            <Typography
              variant={'l'}
              sx={{ fontFamily: 'Suisse-Intl !important' }}
            >
              {i18nLegacy.title}
            </Typography>
            <IconButton
              onClick={() => handleMobileFiltersClose(activeFilters)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        ) : (
          <Stack
            flexDirection={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Typography variant={'l'} componet={'h2'}>
              {i18nLegacy.title}
            </Typography>
            <RedLinkButton
              msg={`${i18nLegacy.reset}`.toLowerCase()}
              onClick={() => handleReset()}
              variant={'link2'}
            />
          </Stack>
        )}
        <FilterSearchBar
          ref={searchRef}
          customOnChange={handleSearchSelection}
          customOnClick={tryComplete}
          label={i18n.searchBar}
        />
        {/* {!isBiennali ? (
          <FilterSelect
            options={orderYears(filter.years)}
            label={i18nLegacy.filterItems.years}
            onSelect={handleYearSelection}
            optionDescriptionCallback={getYear}
            initialValue={yearSelected}
            ref={yearsSelectRef}
            hideResetLabel
          />
        ) : (
          <></>
        )} */}
        <FilterCollapsable
          alreadyOpen={activeFilters?.alphabet ? true : false}
          label={i18n.alphabet}
          labelGridXs={9}
          arrowGridXs={3}
          idBase={'alphabet-dropdown'}
        >
          {filter.alphabet.map((char) => {
            const isSelected = activeFilters?.alphabet === char;
            return (
              <HoveredTypo
                key={char}
                onClick={() => handleAlphabetSelection(char)}
                variant={'body2'}
                style={getHoveredTypoStyle(isSelected)}
              >
                {char}
              </HoveredTypo>
            );
          })}
        </FilterCollapsable>
        {filter.halls?.length ? (
          <FilterCollapsable
            alreadyOpen={activeFilters?.hall ? true : false}
            label={i18n.hall}
            labelGridXs={9}
            arrowGridXs={3}
            idBase={'hall-dropdown'}
          >
            {filter.halls.map((hall) => {
              const isSelected = activeFilters?.hall === hall;
              return (
                <HoveredTypo
                  key={hall}
                  onClick={() => handleHallSelection(hall)}
                  variant={'body2'}
                  style={getHoveredTypoStyle(isSelected)}
                >
                  {hall}
                </HoveredTypo>
              );
            })}
            {!filter.halls.length ? (
              <HoveredTypo variant={'body2'} disabled>
                {i18nLegacy.filterItems.hallNotAvailable}
              </HoveredTypo>
            ) : null}
          </FilterCollapsable>
        ) : null}
        <FilterCollapsable
          alreadyOpen={activeFilters?.exhibitors?.length > 0 ? true : false}
          label={i18nLegacy.filterItems.exhibition}
          collapseStyle={{ width: '100%' }}
          idBase={'exhibition-dropdown'}
        >
          <CheckboxGroup
            options={filter.exhibitions}
            labelKey={'label'}
            idKey={'code'}
            labelPlacement={'end'}
            customFont={'text'}
            onChange={handleExibitionSelection}
            ref={radioGroupRef}
            variant={'black'}
          />
        </FilterCollapsable>{' '}
        {filter.visits?.length ? (
          <FilterCollapsable
            alreadyOpen={activeFilters?.visits?.length > 0 ? true : false}
            label={filter.visits[0].parentLabel}
            collapseStyle={{ width: '100%', marginTop: '8px' }}
            labelGridXs={10}
            arrowGridXs={2}
            idBase={'visits-dropdown'}
          >
            <CheckboxGroup
              options={filter.visits}
              labelKey={'label'}
              idKey={'value'}
              labelPlacement={'end'}
              customFont={'text'}
              onChange={handleVisitSlections}
              ref={radioVisitsRef}
              variant={'black'}
            />
          </FilterCollapsable>
        ) : null}
        <FilterSelect
          options={orderNations(filter.nations)}
          label={i18nLegacy.filterItems.country}
          onSelect={handleNationSelection}
          optionDescriptionCallback={getLocalizedName}
          ref={nationsSelectRef}
          invertResetLabel
        />
        {filter.percorsi_tematici?.length ? (
          <FilterSelect
            options={orderItineraries(filter.percorsi_tematici)}
            label={i18nLegacy.filterItems.itineraries}
            onSelect={handleItinerarySelection}
            optionDescriptionCallback={getLocalizedDescription}
            ref={itinerariesSelectRef}
            invertResetLabel
          />
        ) : null}
        {activeExhibitor ? (
          <>
            <FilterSelect
              initialValue={activeCategory}
              options={getCategories()}
              label={i18nLegacy.filterItems.categories}
              onSelect={handleCategorySelection}
              optionDescriptionCallback={getLocalizedDescription}
            />
          </>
        ) : null}
        {activeCategory ? (
          <>
            <FilterSelect
              ref={productSelectRef}
              initialValue={activeProduct}
              options={getProducts()}
              label={i18nLegacy.filterItems.products}
              onSelect={handleProductSelection}
              optionDescriptionCallback={getLocalizedDescription}
            />
          </>
        ) : null}
        {(incognitoMode && isPostEvent) || isBiennali ? (
          <div
            style={{
              width: '100%',
              ...(isMobileFilters && {
                marginBottom: '80px',
              }),
            }}
          />
        ) : (
          <Box
            bgcolor={'#EEEEEE'}
            border={'1px solid #777777'}
            padding={'25px 15px'}
            width={'100%'}
            sx={{
              ...(isMobileFilters && {
                marginBottom: '80px',
              }),
            }}
          >
            <Stack flexDirection={'row'} gap={'15px'} alignItems={'flex-start'}>
              <InfoOutlinedIcon sx={{ color: '#333333' }} fontSize="small" />
              <Stack>
                <Typography
                  variant="body1-bold"
                  sx={{ mb: '15px', fontFamily: 'Suisse-Intl !important' }}
                >
                  {i18n.greyBox.title}{' '}
                  {incognitoMode || isPostEvent
                    ? ''
                    : `(${totExhibitorsToVisit})`}
                </Typography>
                {incognitoMode ? (
                  <Typography
                    variant="body1"
                    fontSize={'16px  !important'}
                    lineHeight={'24px'}
                  >
                    {i18n.greyBox.notLoggedBody}{' '}
                    <span
                      style={{
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                      onClick={() => (window.location.href = LOGIN_URL)}
                    >
                      {i18n.greyBox.notLoggedLogin} {i18n.greyBox.or}{' '}
                      {i18n.greyBox.notLoggedRegister}
                    </span>
                  </Typography>
                ) : (
                  <Typography
                    variant="body1"
                    fontSize={'16px  !important'}
                    lineHeight={'24px'}
                  >
                    {i18n.greyBox.body} <br />
                    <a
                      style={{
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        '&:hover': {
                          color: 'black',
                        },
                      }}
                      href={PERSONAL_PAGE_URL}
                    >
                      {i18n.greyBox.link}
                    </a>
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Box>
        )}
        {isMobileFilters ? (
          <Paper
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              padding: '20px 30px',
              display: 'flex',
              justifyContent: 'center',
            }}
            elevation={elevationMobileBottom}
          >
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => handleMobileFiltersClose(activeFilters)}
              sx={{ maxWidth: '335px' }}
            >
              {i18n.showResultsBtn}
            </Button>
          </Paper>
        ) : null}
      </Stack>
    </Box>
  );
};
export default ExhibitorsFilters;
